<script lang="ts" setup>
import { ref, computed, nextTick, onUnmounted, watch, inject, Ref } from 'vue';
import { useLocalization } from '@/plugins/localization';
import { useEvents } from '@/plugins/events';
import { useAlerts } from '@/plugins/alerts';
import { LocalizationManager } from '@/components/builder/form/managers/LocalizationManager';
import DynamicForm from '@/modules/low-code/views/DynamicModalForm.vue';
import { FormEntry } from '../../form';
import { DynamicModalType, DynamicActionType, DynamicModalEntry } from '../index';
import { FormBuilderContract } from '@/components/builder/form';
import DynamicFormHelper from '@/modules/low-code/helpers/DynamicFormHelper';

const emit = defineEmits(["action-event", "closing-event", "reset-modal"]);

const props = defineProps({
  "baseBuilder": null,
  "blueprint": null,
  "entry": null,
  "modalEntry": null,
  "currentBlueprint": null,
  "tableRowIndex": null,
  "withValidation": { type: Boolean,  }
});

const { $t } = useLocalization();
const { $events } = useEvents();
const { $alert } = useAlerts();
const localizationManager = new LocalizationManager();

const modal = ref(null);

const baseFormBuilder = inject<Ref<FormBuilderContract>>('baseFormBuilder');

const title = computed(() => localizationManager.translate(props.blueprint.formTitle));
const licence = computed(() => props.entry.module);
const endpointId = computed(() => props.entry.formId);
const actions = computed(() => props.blueprint.dynamicActions);
const buttonMode = computed(() => props.blueprint.modalMode == 'Button');
const dynamicFormRef = ref<any>(null);
const isLoaded = ref<boolean>(false);

const showModal = async () =>
{
    if (buttonMode.value)
    {
        await executeScript(props.blueprint.script, props.blueprint.scriptConfigId, props.baseBuilder, props.withValidation);

        return;
    }

    $events.$on('dynamic-modal-hide', hideModal);

    nextTick(() =>
    {
        modal.value.show();
        isLoaded.value = true;
    });
};

const hideModal = async () =>
{
    modal.value.hide();
};

const actionEvent = async (action: DynamicActionType) =>
{
    const currentBuilder = await dynamicFormRef.value.getBuilder();

    if (action.closingModal)
    {
        isLoaded.value = false;

        if (!await validateForm(currentBuilder))
            return;

        emit('action-event', action, currentBuilder);
    }
    else
    {
        await executeScript(action.script, action.scriptConfigId, currentBuilder, true);
    }

    modal.value.hide();
};

const validateForm = async (builder: any) =>
{
    try
    {
        await DynamicFormHelper.validateForm(licence.value, endpointId.value, props.modalEntry, builder, buttonMode.value ? props.currentBlueprint : props.blueprint);

        return true;
    }
    catch (ex: any)
    {
        if (ex.code === 422)
        {
            const errors: Record<string, string[]> = {};

            const modalBuilder = await dynamicFormRef.value.getBuilder();

            Object.entries(ex.data.errors).forEach(([key, value]) =>
            {
                if (key.toLowerCase() === 'registerid')
                {
                    const registerControl =
                        modalBuilder.schema.components('register')[0] ??
                        props.baseBuilder.schema.components('register')[0];

                    if (registerControl)
                        errors[`${registerControl.name.toLowerCase()}.value`] = value as string[];

                    return;
                }

                const keyWithDot =
                    key.includes('.value') || key.includes('.custom')
                        ? key.toLowerCase()
                        : `${key.toLowerCase()}.value`;

                errors[keyWithDot] = value as string[];
            });

            modalBuilder.setEntryErrors(errors);
            props.baseBuilder.setEntryErrors(errors);

            const errorMessage = ex.data.errors['popup-message']?.[0] ?? $t('[[[Nie wszystkie pola zostały wypełnione prawidłowo]]]');

            $alert.warning(errorMessage);
        }

        return false;
    }
};

const executeScript = async (script: any, scriptConfigId: string, builder: any, withValidation: boolean) =>
{
    if (script?.key)
    {
        try
        {
            const formData = await DynamicFormHelper.getFormData(licence.value, endpointId.value, props.modalEntry, builder, baseFormBuilder?.value, script.key, scriptConfigId, buttonMode.value ? props.currentBlueprint : props.blueprint, null, props.tableRowIndex, withValidation);

            emit('closing-event', formData);
        }
        catch (ex: any)
        {
            if (ex.code === 422)
            {
                const errors: Record<string, string[]> = {};

                const scriptBuilder = await dynamicFormRef.value.getBuilder();

                Object.entries(ex.data.errors).forEach(([key, value]) =>
                {
                    if (key.toLowerCase() === 'registerid')
                    {
                        const registerControl =
                            scriptBuilder.schema.components('register')[0] ??
                            props.baseBuilder.schema.components('register')[0];

                        if (registerControl)
                            errors[`${registerControl.name.toLowerCase()}.value`] = value as string[];

                        return;
                    }

                    const keyWithDot =
                    key.includes('.value') || key.includes('.custom')
                        ? key.toLowerCase()
                        : `${key.toLowerCase()}.value`;

                    errors[keyWithDot] = value as string[];
                });

                scriptBuilder.setEntryErrors(errors);
                props.baseBuilder.setEntryErrors(errors);

                const errorMessage = ex.data.errors['popup-message']?.[0] ?? $t('[[[Nie wszystkie pola zostały wypełnione prawidłowo]]]');

                $alert.warning(errorMessage);
            }
        }
    }
};

const getTranslatedText = (text: Record<string, string>) =>
{
    return localizationManager.translate(text);
};

watch(() => (modal.value?.visibilityState()), async () =>
{
    if (isLoaded.value == true && modal.value?.visibilityState() == false)
    {
        emit('reset-modal');
    }
}, {
    immediate: true
});

onUnmounted(() =>
{
    $events.$off('dynamic-modal-hide', hideModal);
});

defineExpose({
    showModal,
    hideModal
});
</script>

<template>
    <ideo-modal ref="modal" :title="title" size="xl" centered scrollable>
        <DynamicForm
            :licence="licence" :endpoint-id="endpointId" :base-builder="baseBuilder" :base-entry="entry" :modal-entry="modalEntry" :current-blueprint="currentBlueprint"
            :blueprint="blueprint" ref="dynamicFormRef" :table-row-index="tableRowIndex" :with-validation="withValidation"
        ></DynamicForm>
        <template #modal-footer="{ cancel }">
            <template v-for="(action, index) in actions" :key="index">
                <ideo-button :variant="(action.buttonStyle?.key ?? 'success').toLowerCase()" @click="actionEvent(action)">{{ getTranslatedText(action.name) }}</ideo-button>
            </template>
            <ideo-button variant="secondary" @click="cancel">{{ $t('[[[Anuluj]]]') }}</ideo-button>
        </template>
    </ideo-modal>
</template>
